import Extension from "../base/extension";

/**
 * ScrollLock
 * =============================================================================
 * Stops the page scrolling behind any open overlay: Bootstrap offcanvas, native
 * <dialog>, or anything that opts in via lock()/unlock().
 *
 * WHY THIS EXISTS
 * Bootstrap's own lock (and `overflow: hidden` on <body> generally) does not
 * hold on iOS Safari: touch scrolling still moves the page underneath, and once
 * the overlay closes the page has silently scrolled somewhere else. It is worse
 * than cosmetic on a cart or filter drawer, where the shopper's place in a long
 * collection is lost.
 *
 * `overscroll-behavior: contain` on the panel is NOT sufficient on its own.
 * WebKit's implementation is partial (BCD, checked 2026-08-05): "the property
 * has no effect on scroll containers that have no scrollable overflow", so a
 * SHORT panel (near-empty cart, few filters) chains its scroll straight through
 * to the page while a long one does not. That inconsistency is exactly the
 * "sometimes it works" iOS behaviour this replaces. We still set it in CSS as a
 * complement, but the lock below is what actually holds.
 *
 * THE TECHNIQUE
 * Pin <body> with `position: fixed` and a negative `top` equal to the current
 * scroll offset, then restore the offset on release. This is the only approach
 * that holds across all iOS versions, because the page genuinely has nowhere to
 * scroll rather than merely being told not to.
 *
 * Locks are reference-counted by element, so stacked overlays (offcanvas with a
 * dialog on top, or nested dialogs) release the page only when the last one
 * closes. Replaces the CDN `body-scroll-lock` + inline script that used to live
 * in sections/offcanvas-cart.liquid.
 */
class ScrollLock extends Extension {
  #locks = new Set();
  #scrollY = 0;
  #paddedByUs = false;
  #themeColor = null;
  #themeColorWasAbsent = false;

  init() {
    // Every overlay in the theme is now a modal <dialog>, so the observer below
    // is the only wiring needed. (The Bootstrap .bs.modal and .bs.offcanvas
    // listeners went with those components, 2026-08-06.)
    this.#observeDialogs();

    // Safety net: never leave <body> pinned across a navigation. pagehide also
    // covers the bfcache path, which `beforeunload` misses.
    window.addEventListener("pagehide", () => this.releaseAll());
  }

  /**
   * Native <dialog> has no bubbling open event (`close` does not bubble
   * either), so watch the `open` attribute instead. One observer catches every
   * modal dialog however it was opened, including by app or third-party code.
   */
  #observeDialogs() {
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        const el = record.target;
        // <details> also carries [open]; only dialogs concern us.
        if (el.tagName !== "DIALOG") continue;
        // A non-modal show() leaves the page interactive, so it must not lock.
        if (el.open && el.matches(":modal")) this.lock(el);
        else this.unlock(el);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["open"],
      subtree: true,
    });
  }

  lock(element) {
    if (!element) return;
    this.#prune();
    this.#locks.add(element);
    if (this.#locks.size === 1) this.#apply();
  }

  unlock(element) {
    if (!element) return;
    this.#locks.delete(element);
    this.#prune();
    if (this.#locks.size === 0) this.#release();
  }

  releaseAll() {
    this.#locks.clear();
    this.#release();
  }

  /**
   * Drop lock holders that were removed from the DOM while open (a section
   * re-render can take an overlay with it, and no attribute mutation fires for
   * that), so a stale entry can never pin the page indefinitely.
   */
  #prune() {
    for (const element of this.#locks) {
      if (!element.isConnected) this.#locks.delete(element);
    }
  }

  #apply() {
    const body = document.body;
    if (body.classList.contains("scroll-locked")) return;

    this.#scrollY = window.scrollY;

    // Pinning <body> removes the scrollbar, so reserve its width to stop the
    // page jolting sideways. Bootstrap's ScrollBarHelper already does this for
    // its own components; only pad when it has not, or the gap doubles.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0 && !body.style.paddingRight) {
      body.style.paddingRight = `${gap}px`;
      this.#paddedByUs = true;
    }

    body.style.top = `-${this.#scrollY}px`;
    // The CSS needs the same offset to size the pinned box: <body> starts this
    // far above the viewport top, so it has to be that much taller to end at the
    // bottom of it. See the `height` in `body.scroll-locked`.
    body.style.setProperty("--scroll-lock-y", `${this.#scrollY}px`);
    body.classList.add("scroll-locked");
    // Also on <html>: pinning <body> takes it out of flow, so the root element
    // collapses to zero height. The CSS gives it a viewport-height box and the
    // dimmed page colour, which keeps the document itself consistent.
    document.documentElement.classList.add("scroll-locked");
    this.#tintChrome();
  }

  /**
   * Point `theme-color` at the dimmed page colour for as long as the overlay is
   * up, and put it back afterwards.
   *
   * The strips iOS reserves behind the dynamic island and the address bar are
   * browser CHROME, not page. Measured on an iPhone 16 Pro (2026-08-06), the
   * document is byte-identical between the renders that look right and the ones
   * that look wrong: same root height and colour, same body box, `innerHeight`
   * and `visualViewport.height` both pinned at 714 throughout. No CSS can reach
   * them, which is why sizing the root and then the body each fixed a real bug
   * without curing the symptom. Safari samples a page colour for that chrome on
   * its own schedule, so opening a dialog does not prompt a re-sample and the
   * strips keep whatever they last decided on.
   *
   * `theme-color` is the only supported way to state it outright. Driving it
   * from the root's own computed background means the strips and the locked page
   * cannot drift apart: retune `--scroll-lock-bg` and both follow.
   */
  #tintChrome() {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
      this.#themeColorWasAbsent = true;
    }
    this.#themeColor = meta.getAttribute("content");
    // Read after the class is on, so this is the locked colour, not the page's.
    meta.setAttribute(
      "content",
      getComputedStyle(document.documentElement).backgroundColor
    );
  }

  #restoreChrome() {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    if (this.#themeColorWasAbsent) {
      meta.remove();
      this.#themeColorWasAbsent = false;
      return;
    }
    if (this.#themeColor === null) meta.removeAttribute("content");
    else meta.setAttribute("content", this.#themeColor);
  }

  #release() {
    const body = document.body;
    if (!body.classList.contains("scroll-locked")) return;

    body.classList.remove("scroll-locked");
    document.documentElement.classList.remove("scroll-locked");
    this.#restoreChrome();
    body.style.top = "";
    body.style.removeProperty("--scroll-lock-y");
    if (this.#paddedByUs) {
      body.style.paddingRight = "";
      this.#paddedByUs = false;
    }

    // Restore instantly: a smooth scroll here would animate the page back into
    // place after the overlay has already gone.
    window.scrollTo({ top: this.#scrollY, behavior: "instant" });
  }
}

export default ScrollLock;
